import type { ReactNode } from 'react'
import { BsChevronDown, BsChevronUp } from 'react-icons/bs'

interface Props {
    icon: ReactNode
    title: string
    /** Quanto já foi consumido no período. */
    current: number
    /** Teto do plano — `'unlimited'` quando não há. */
    max: number | 'unlimited'
    /** Sufixo do número, quando a unidade não é óbvia (ex.: "min"). */
    unit?: string
    isNearLimit?: boolean
    isAtLimit?: boolean
    expanded: boolean
    onToggle: () => void
}

/**
 * Card de consumo do plano.
 *
 * Antes mostrava só o título e uma seta: era preciso clicar em cada um para
 * descobrir quanto havia sido usado, e quatro deles ocupavam a faixa superior
 * inteira sem informar nada. Agora o número e a barra ficam à vista, e o clique
 * serve apenas para abrir o detalhamento e a compra de pacotes.
 *
 * O conteúdo é empilhado, não distribuído na horizontal: o card vive numa
 * coluna de 1/4 da largura, onde número grande, limite e "restam X" na mesma
 * linha não cabem — disputando espaço, empurravam o texto e a seta para fora
 * da borda. Cada informação tem sua linha, e `min-w-0` + `truncate` seguram o
 * título longo.
 *
 * Com limite ilimitado não há barra nem "restam X" — não existe o que medir.
 */
export default function UsageCard({
    icon,
    title,
    current,
    max,
    unit,
    isNearLimit = false,
    isAtLimit = false,
    expanded,
    onToggle,
}: Props) {
    const unlimited = max === 'unlimited'
    const remaining = unlimited
        ? null
        : Math.max(0, (max as number) - current)
    const percentage = unlimited
        ? 0
        : Math.min(
              100,
              Math.round((current / Math.max(1, max as number)) * 100)
          )

    const barTone = isAtLimit
        ? 'bg-error'
        : isNearLimit
          ? 'bg-warning'
          : 'bg-secondary'

    const numberTone = isAtLimit
        ? 'text-error'
        : isNearLimit
          ? 'text-warning'
          : 'text-base-content'

    const format = (value: number) => value.toLocaleString('pt-BR')

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className={`flex w-full min-w-0 flex-col gap-2.5 rounded-xl border p-4 text-left transition-colors ${
                expanded
                    ? 'border-secondary/40 bg-secondary/5'
                    : 'border-base-300 bg-base-100 hover:bg-base-200/50'
            }`}
        >
            <div className="flex w-full min-w-0 items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                    {icon}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-base-content">
                    {title}
                </span>
                <span className="shrink-0 text-xs text-base-content/40">
                    {expanded ? <BsChevronUp /> : <BsChevronDown />}
                </span>
            </div>

            {unlimited ? (
                <div className="min-w-0">
                    <p className={`truncate text-xl font-bold ${numberTone}`}>
                        {format(current)}
                        {unit ? ` ${unit}` : ''}
                    </p>
                    <p className="truncate text-xs text-base-content/50">
                        Sem limite no plano
                    </p>
                </div>
            ) : (
                <div className="flex min-w-0 flex-col gap-2">
                    <p className="min-w-0 truncate">
                        <span className={`text-xl font-bold ${numberTone}`}>
                            {format(current)}
                        </span>
                        <span className="text-xs text-base-content/40">
                            {' '}
                            / {format(max as number)}
                            {unit ? ` ${unit}` : ''}
                        </span>
                    </p>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-base-300">
                        <div
                            className={`h-full rounded-full transition-all ${barTone}`}
                            style={{ width: `${percentage}%` }}
                        />
                    </div>
                    <p className="truncate text-xs text-base-content/50">
                        {remaining === 0
                            ? 'Limite atingido'
                            : `Restam ${format(remaining as number)}`}
                    </p>
                </div>
            )}
        </button>
    )
}
